import React from 'react';
import { message, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Danger, RepeateMusic } from 'iconsax-react';
import { useReturnBusinessInfoMutation } from '../../store/services/business-requests';
import Button from '../atoms/Button';
import FormField from '../FormField/FormField';
import TitleText from '../atoms/TitleText';
import InfoText from '../atoms/InfoText';

const REASON_MIN = 20;
const REASON_MAX = 500;
/** English, Arabic, digits, special characters */
const REASON_PATTERN = /^[\p{L}\p{N}\p{P}\p{S}\s]*$/u;

const returnReasonSchema = (t: (key: string) => string) =>
  z.object({
    reason: z
      .string()
      .trim()
      .min(REASON_MIN, t('home.reasonValidationMin'))
      .max(REASON_MAX, t('home.reasonValidation'))
      .regex(REASON_PATTERN, t('home.reasonValidation')),
  });

type ReturnFormValues = z.infer<ReturnType<typeof returnReasonSchema>>;

interface ReturnModalProps {
  isModalOpen: boolean;
  handleModalClose: (val: boolean) => void;
  requestId: string;
}

const ReturnModal = ({
  isModalOpen,
  handleModalClose,
  requestId,
}: ReturnModalProps) => {
  const { t } = useTranslation();
  const [returnBusinessInfo] = useReturnBusinessInfoMutation();

  const methods = useForm<ReturnFormValues>({
    resolver: zodResolver(returnReasonSchema(t)),
    defaultValues: { reason: '' },
    mode: 'onTouched',
  });

  const onSubmit = async (data: ReturnFormValues) => {
    if (!requestId) return;
    try {
      await returnBusinessInfo({
        returnReason: data.reason.trim(),
        id: requestId,
      }).unwrap();
      message.success(t('home.requestReturnedToUserToEdit'));
      methods.reset();
      handleModalClose(true);
    } catch (error) {
      message.error(t('home.somethingWentWrong'));
    }
  };

  const handleClose = () => {
    methods.reset();
    handleModalClose(false);
  };

  return (
    <Modal
      open={isModalOpen}
      onCancel={handleClose}
      closable={false}
      footer={null}
      centered
      destroyOnHidden
      width={600}
      data-test="return-modal"
    >
      <div
        className="flex flex-col items-center justify-center gap-4 py-2"
        data-test="return-modal-content"
      >
        {/* Icon */}
        <div
          className="bg-amber-50 w-12 h-12 rounded-xl flex justify-center items-center"
          data-test="return-modal-icon"
        >
          <Danger size={24} color="#D97706" />
        </div>

        {/* Title and Description */}
        <div className="flex flex-col gap-1 items-center text-center">
          <TitleText
            text={t('home.returnRequest')}
            className="!font-semibold"
          />
          <InfoText
            text={t('home.returnRequestDescription')}
            maxLength="FULL"
          />
        </div>

        {/* Reason Textarea */}
        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(onSubmit)}
            className="w-full mt-2"
            data-test="return-modal-reason-field"
          >
            <FormField
              id="return-modal-reason"
              name="reason"
              type="textarea"
              label={t('home.reason')}
              placeholder={t('home.placeholder')}
              maxLength={REASON_MAX}
              helperText={methods.formState.errors.reason?.message}
              showMaxLength
              data-test="return-modal-reason-textarea"
            />

            {/* Buttons */}
            <div
              className="flex justify-center w-full gap-3 mt-2"
              data-test="return-modal-actions"
            >
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                data-test="return-modal-cancel-btn"
              >
                {t('home.cancel')}
              </Button>
              <Button
                type="submit"
                variant="warning"
                icon={<RepeateMusic size={18} color="#ffffff" />}
                className="w-40"
                data-test="return-modal-submit-btn"
              >
                {t('home.return')}
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </Modal>
  );
};

export default ReturnModal;
